import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import PropertySetup from "@/components/PropertySetup";
import JourneyStepper from "@/components/JourneyStepper";
import ReadingForm from "@/components/ReadingForm";
import LatestReadingCard from "@/components/LatestReadingCard";
import ActionPlan from "@/components/ActionPlan";
import ReadingHistory from "@/components/ReadingHistory";
import MitigationCard from "@/components/MitigationCard";
import AssistanceScreening from "@/components/AssistanceScreening";
import ShareRecord from "@/components/ShareRecord";
import CertifiedProfessional from "@/components/CertifiedProfessional";
import LibraryLendCard from "@/components/LibraryLendCard";
import RemindersCard from "@/components/RemindersCard";
import { t } from "@/lib/i18n";

/**
 * Frontend mirror of backend action_steps_for().
 * Kept in sync intentionally so we can render the personalized plan without
 * an extra API round-trip. If the two ever diverge, the backend is source of truth.
 */
const ACTION_ITEMS = {
  "below-action": [
    { id: "retest_2yr", title: t("Retest every 2 years"), description: t("Radon levels change with seasons, renovations, and time. A repeat short-term test in 2 years is a sensible cadence.") },
    { id: "long_term_test", title: t("Consider a long-term (90-day) test"), description: t("A long-term test averages seasonal variation for a more reliable picture.") },
    { id: "save_record", title: t("Save this reading to your Radon Record"), description: t("Keep a permanent history of every test on this property.") },
  ],
  "consider": [
    { id: "confirm_result", title: t("Confirm the result with a second test"), description: t("Run either a follow-up short-term test or a long-term (90-day) kit.") },
    { id: "consider_mitigation", title: t("Consider mitigation"), description: t("The EPA suggests fixing homes above 2.0 pCi/L when practical. Talk to a certified radon professional.") },
    { id: "seal_cracks", title: t("Seal foundation cracks and pipe penetrations"), description: t("An easy interim step that reduces the pathways radon can use to enter your home.") },
    { id: "save_record", title: t("Save this reading to your Radon Record"), description: t("Keep a permanent history of every test on this property.") },
  ],
  "action": [
    { id: "confirm_result", title: t("Confirm the result if required by your test method"), description: t("Short-term tests are often confirmed with a second short-term or a long-term test.") },
    { id: "contact_pro", title: t("Contact a qualified radon-mitigation professional"), description: t("Find a certified mitigator through AARST/NRPP or your state radon program.") },
    { id: "reduce_level", title: t("Reduce the radon level in your home"), description: t("A typical sub-slab depressurization system costs $800–$1,500 and reduces radon by 50–99%.") },
    { id: "post_test", title: t("Conduct a post-mitigation test"), description: t("Verify the system is working after installation, then again every 2 years.") },
    { id: "save_record", title: t("Save this reading to your Radon Record"), description: t("Keep a permanent history of every test on this property.") },
  ],
  "high": [
    { id: "confirm_short", title: t("Run a follow-up short-term test"), description: t("Verify the elevated reading before scheduling mitigation.") },
    { id: "contact_pro_soon", title: t("Contact a certified mitigator this week"), description: t("Ask for AARST-NRPP or state credentials, and request a written proposal.") },
    { id: "reduce_occupancy", title: t("Reduce time in the highest-affected room until mitigated"), description: t("Sleeping areas and basements are the highest priority to reduce exposure.") },
    { id: "post_test", title: t("Post-mitigation test after installation"), description: t("Confirm the system reduces radon to as low as reasonably achievable.") },
    { id: "save_record", title: t("Save this reading to your Radon Record"), description: t("Keep a permanent history of every test on this property.") },
  ],
  "very-high": [
    { id: "immediate_action", title: t("Ventilate the highest-affected area now"), description: t("Open windows and run fans in basements or slab-on-grade lower levels.") },
    { id: "confirm_urgent", title: t("Run a confirmatory short-term test"), description: t("A second short-term test verifies extreme readings before mitigation.") },
    { id: "contact_pro_urgent", title: t("Contact a certified mitigator immediately"), description: t("Explain the reading. Many will prioritize very high readings.") },
    { id: "reduce_occupancy", title: t("Minimize occupancy in the tested space until mitigated"), description: t("Especially avoid sleeping in the tested area if levels remain this high.") },
    { id: "post_test_urgent", title: t("Post-mitigation test within 30 days of install"), description: t("Confirm the reduction and set a long-term monitoring plan.") },
    { id: "save_record", title: t("Save this reading to your Radon Record"), description: t("Keep a permanent history of every test on this property.") },
  ],
};

const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [readings, setReadings] = useState([]);
  const [journey, setJourney] = useState({ steps: [], latest_level: null });
  const [libraryLend, setLibraryLend] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [p, r, j, l] = await Promise.all([
        api.get("/property"),
        api.get("/readings"),
        api.get("/journey"),
        api.get("/library-lend"),
      ]);
      setProperty(p.data);
      setReadings(r.data);
      setJourney(j.data);
      setLibraryLend(l.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // If ?add=1 in URL, scroll to the reading form on mount.
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setTimeout(() => {
        document
          .querySelector("[data-testid='reading-form']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        const next = new URLSearchParams(searchParams);
        next.delete("add");
        setSearchParams(next, { replace: true });
      }, 300);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const latest = readings[0];
  const actionItems = latest ? ACTION_ITEMS[latest.classification.level] : [];

  const onReadingCreated = async (newReading) => {
    setReadings((prev) => [newReading, ...prev]);
    // Refresh journey (mitigation/verify steps change with reading kinds)
    try {
      const { data } = await api.get("/journey");
      setJourney(data);
    } catch {}
    setTimeout(() => {
      document
        .querySelector("[data-testid='latest-reading-card']")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const onReadingDelete = async (id) => {
    try {
      await api.delete(`/readings/${id}`);
      setReadings((prev) => prev.filter((r) => r.id !== id));
      const { data } = await api.get("/journey");
      setJourney(data);
    } catch {}
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-8">
      <div className="fade-up">
        <p className="eyebrow">{t("Your Radon Record")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#1C201F] leading-tight">
          {t("Welcome back,")}{" "}
          <span className="italic text-[#2F524A]">
            {user?.name?.split(" ")[0] || "neighbor"}
          </span>
          .
        </h1>
        <p className="text-[#5C6661] mt-2 max-w-2xl">
          {t("Track every radon reading, action, and mitigation on this property in one place. The EPA recommends fixing homes at or above 4.0 pCi/L, but no level of radon is completely safe.")}
        </p>
      </div>

      {!loading && journey.steps.length > 0 && (
        <div className="fade-up delay-1">
          <JourneyStepper steps={journey.steps} />
        </div>
      )}

      <div className="fade-up delay-2">
        {!loading && (
          <PropertySetup property={property} onSaved={(p) => setProperty(p)} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-8">
          {(user?.user_type === "library" || libraryLend) && (
            <LibraryLendCard existing={libraryLend} onLent={setLibraryLend} />
          )}
          <ReadingForm hasProperty={!!property} onCreated={onReadingCreated} />
          <MitigationCard readings={readings} onChanged={loadAll} />
          <ReadingHistory readings={readings} onDelete={onReadingDelete} />
        </div>
        <div className="lg:col-span-5 space-y-8">
          <LatestReadingCard reading={latest} />
          <RemindersCard />
          {!loading && <ActionPlan items={actionItems || []} actionLevel={latest?.classification?.level} />}
          {!loading && <AssistanceScreening key={latest?.id || "none"} latestReading={latest} />}
          <ShareRecord />
          <CertifiedProfessional state={property?.state} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
